import React, { useEffect, useState } from "react";
import { Alert, Button, Select, Upload, Form, Input, message, Space } from "antd";
import { useDrawer } from "../../../context/drawer";
import { PlusOutlined } from "@ant-design/icons";
import { useCategoryDetailMutation } from "../../../services/userAuthAPI";
import { useSelector } from "react-redux";

export default function UpdateForm() {
  const { drawerData, toggleDrawer } = useDrawer();
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [updateImage, setUpdateImage] = useState(false);
  const [categoryDetail] = useCategoryDetailMutation();
  const [imgError, setImgError] = useState({ message: null, status: null });
  const [slug, setSlug] = useState("");
  const [form] = Form.useForm();
  const { token } = useSelector((state) => state.auth);

  // Handle name change and update slug
  const handleNameChange = (e) => {
    const newSlug = e.target.value.toLowerCase().replace(/\s+/g, "-");
    setSlug(newSlug);
    form.setFieldsValue({ slug: newSlug });
  };

  // Image upload validation
  const beforeUpload = (file) => {
    const isValidType = ["image/jpeg", "image/png", "image/avif"].includes(file.type);
    const isLt300KB = file.size / 1024 < 300;

    if (!isValidType) {
      setImgError({ message: "You can only upload JPG/PNG/AVIF files!", status: true });
      message.error("Invalid file type!");
      return false;
    }

    if (!isLt300KB) {
      setImgError({ message: "Image size must be less than 300KB!", status: true });
      message.error("Image too large!");
      return false;
    }

    setImgError({ message: null, status: false });
    return true;
  };

  // Handle form submission
  const onFinish = async (values) => {
    console.log("Form Values:", values);
    try {
      const formData = new FormData();
      formData.append("category_Id", values.id);
      formData.append("category_name", values.name);
      formData.append("gander", values.category);
      formData.append("category_desc", values.description);
      formData.append("action", "update");
      formData.append("is_active", "1");
      formData.append("category_slugs", values.slug);
      formData.append("admin_token", "OdRLLsX46k3L8EwZgpNlVK5tKiRaghecPNH");

      // Append image only if updateImage is true and fileList contains an image
      // Append image only if updateImage is true
      if (updateImage && fileList.length > 0) {
        formData.append("category_img", fileList[0].originFileObj);
      } else if (!updateImage && fileList.length > 0) {
        // Append existing image URL to preserve it
        formData.append("category_img", fileList[0].url);
      }


      setLoading(true);
      const response = await categoryDetail(formData);

      if (response.data) {
        setLoading(false);
        if (response.data.status === "Failed Name") {
          message.error(response.data.message);
          setImgError({ message: response.data.message, status: true });
        } else {
          form.resetFields();
          toggleDrawer();
          message.success("Update successful!");
          setImgError({ message: null, status: null });
        }
      }
    } catch (error) {
      setLoading(false);
      message.error("Update failed!");
      console.error("Error making POST request", error);
    }
  };

  useEffect(() => {
    if (drawerData?.value) {
      console.log("Drawer Data:", drawerData.value);
      form.setFieldsValue({
        id: drawerData.value.category_Id,
        name: drawerData.value.category_name,
        description: drawerData.value.category_desc,
        category: drawerData.value.gander,
        slug: drawerData.value.category_name?.toLowerCase().replace(/\s+/g, "-"),
      });

      if (drawerData.value.category_img) {
        setFileList([
          {
            uid: "-1",
            name: "existing image",
            status: "done",
            url: drawerData.value.category_img,
            thumbUrl: drawerData.value.category_img,
          },
        ]);
      }
      setUpdateImage(false);
    }
  }, [drawerData, form]);

  const handleChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
    setUpdateImage(true);
  };

  const uploadButton = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Upload</div>
    </div>
  );

  return (
    <div className="drawerForm">
      <Form form={form} layout="vertical" onFinish={onFinish} autoComplete="off">
        {imgError.status && <Alert message={imgError.message} type="error" />}

        {/* Image Upload */}
        <Form.Item label="Upload Image" name="cat_image">
          <Upload
            listType="picture-card"
            fileList={fileList}
            onChange={handleChange}
            beforeUpload={beforeUpload}
            accept=".jpeg, .jpg, .png, .avif, .webp"
            maxCount={1}
          >
            {fileList.length >= 1 ? null : uploadButton}
          </Upload>
        </Form.Item>

        {/* Category Selection */}
        <Form.Item
          name="category"
          label="Category"
          rules={[{ required: true, message: "Please select a category!" }]}
        >
          <Select placeholder="Select a category">
            <Select.Option value="men">Men</Select.Option>
            <Select.Option value="women">Women</Select.Option>
            <Select.Option value="accessories">Accessories</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="id"
          hidden
        >
          <Input />
        </Form.Item>

        {/* Category Type */}
        <Form.Item
          name="name"
          label="Category Type"
          rules={[{ required: true, message: "Please enter category type!" }]}
        >
          <Input placeholder="Category Type" onChange={handleNameChange} />
        </Form.Item>

        {/* Slug */}
        <Form.Item
          name="slug"
          label="Category Slug"
          rules={[{ required: true, message: "Slug is required!" }]}
        >
          <Input placeholder="Category Slug" value={slug} disabled />
        </Form.Item>

        {/* Description */}
        <Form.Item
          name="description"
          label="Category Description"
          rules={[{ required: true, message: "Description is required!" }]}
        >
          <Input.TextArea
            showCount
            maxLength={150}
            style={{ height: 100, resize: "none" }}
            placeholder="Category Description"
          />
        </Form.Item>

        {/* Action Buttons */}
        <Form.Item style={{ marginTop: "20px" }}>
          <Space>
            <Button type="primary" loading={loading} htmlType="submit">
              Submit
            </Button>
            <Button danger onClick={toggleDrawer}>
              Cancel
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
}
